/**
 * Fetches the revision snapshot for a given WFS base URL.
 *
 * @param {string} baseUrl  The root URL of the WFS instance (e.g. "https://host/wfs-oneway").
 *                          The "/revision" path will be appended automatically.
 * @param {string} authToken  Bearer token for the Authorization header.
 * @returns {Promise<Array<{layerId: number, revision: number}>>}
 *   A flat array of { layerId, revision } objects, merging parentLayerRevisions,
 *   otherLayerRevisions and relationLayerRevisions — without category labels.
 */
export async function fetchRevisions(baseUrl, authToken) {
  const url = baseUrl + "/revision";
  const response = await fetch(url, {
    headers: {
      Authorization: "Bearer " + authToken,
    },
  });
  if (!response.ok) {
    throw new Error(
      `Failed to fetch revisions from ${url}: ${response.status} ${response.statusText}`,
    );
  }
  const data = await response.json();
  const all = [
    ...(data.parentLayerRevisions ?? []),
    ...(data.otherLayerRevisions ?? []),
    ...(data.relationLayerRevisions ?? []),
  ];
  // Deduplicate by layerId — last occurrence wins.
  const byLayerId = new Map(all.map((lr) => [lr.layerId, lr]));
  return Array.from(byLayerId.values());
}

/**
 * Converts an array of { layerId, revision } objects into "layerId:revision"
 * strings suitable for use as repeated REVISION= query parameters.
 *
 * @param {Array<{layerId: number, revision: number}>} layerRevisions
 * @returns {string[]}  e.g. ["123:456", "789:12"]
 */
export function buildRevisionParams(layerRevisions) {
  return layerRevisions.map(
    ({ layerId, revision }) => `${layerId}:${revision}`,
  );
}
